/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, ReactElement, useEffect, useRef } from 'react';
import {
  ColorValue,
  findNodeHandle,
  FlatList,
  FlatListProps,
  ListRenderItem,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TargetedEvent,
  TouchableHighlight,
  View,
  ViewStyle,
} from 'react-native';

import { helpers } from '@helpers';
import { useContextReference, useIndexedStateReference } from '@hooks';

import { FocusableFlatListItem } from '../FocusableFlatListItem/FocusableFlatListItem';

/**
 * A wrapper around FlatList which will handle internal focus of its elements using dynamic refs.
 * FocusableFlatlist also sets default props on FlatList for tv focus handling. The wrapper will pass
 * any unrecognized props to the FlatList child, for setting specific properties on it.
 * The list can also scroll elements in a fixed or floating focus way. To set the floating focus,
 * use the focusPositions prop and set it to how many elements can have focus on screen without
 * requiring scrolling in the list. To fix focus on the first element position, you can use
 * focusPositions = 1 (the default).
 *
 * To allow the list to start offset from the edges, you can set paddingEnd and paddingStart values.
 * Note that this only affects alignment of the items, not what is visible onscreen.
 *
 * Items can be basic nodes like TWCText, and the list will wrap them in focusable elements if wrapItem
 * prop is true (the default). You can pass itemStyle and itemStyleFocused props to style these elements
 * as needed.
 * If you do NOT want the list to wrap your items, if they are already handling their own focus style for
 * example, then set wrapItem to false.
 *
 * The list will auto-focus the first item when it is constructed on the page. To prevent this default
 * behavior, you can set autoFocus=false.
 *
 * To use/store which index is focused in the list in the parent component, pass a callback to focusIndexChanged
 * which takes the new focused index as a parameter.
 *
 * To set an initial offset into the list, pass both initialScrollIndex to tell the flat list where to start
 * rendering items, and getItemLayout to tell the list the size/position of list elements. See the flatlist
 * reference for these values: https://reactnative.dev/docs/flatlist
 */
export const FocusableFlatList = forwardRef<TouchableHighlight, FocusableFlatListProps>(
  (
    {
      horizontal,
      data,
      keyExtractor,
      itemStyle,
      itemStyleFocused,
      itemUnderlayColor,
      renderItem,
      style,
      wrapItem = true,
      shouldUseContextReference = true,
      paddingEnd = 0,
      paddingStart = 0,
      focusPositions = 1,
      focusIndexChanged,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      nextFocusUp,
      initialScrollIndex,
      testID,
      onFocus,
      onRef,
      listGainedFocus,
      importantForAccessibility,
      ...properties
    },
    reference,
  ) => {
    const flatListReference = useRef<FlatList>(null);
    const firstFocusSet = useRef(false);
    const listHasFocus = useRef(false);
    const [stateReferences, , setStateReferences] = useIndexedStateReference<TouchableHighlight | null>({});

    const focusIndex = useRef(initialScrollIndex ? initialScrollIndex : 0);
    const focusPosition = useRef(0);

    const contextReference = useContextReference<TouchableHighlight>(testID);

    const focusToIndex = (index: number, forceFocus = false) => {
      if (stateReferences.current && data) {
        if (forceFocus || listHasFocus.current) {
          const newReference = stateReferences.current[index];

          if (newReference) {
            helpers.FOCUS_NOW(newReference);
          }
        } else {
          focusIndex.current = index;
          setOffset(true);
        }
      }
    };

    const scrollToIndex = (index: number, animated?: boolean | null) => {
      if (flatListReference.current && data) {
        flatListReference.current.scrollToIndex({
          index,
          animated,
          viewOffset: 0,
          viewPosition: 0,
        });
      }
    };

    useEffect(() => {
      if (Platform.OS === 'web' && flatListReference.current) {
        const node = findNodeHandle(flatListReference.current);

        if (node) {
          // Set FlatList spatial navigation action as focus to avoid scroll on up
          node.style.setProperty('--spatial-navigation-action', 'focus');
        }
      }

      if (onRef) {
        onRef({
          scrollToIndex,
          focusToIndex,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (firstFocusSet.current) {
        const lastFocusPos = focusIndex.current - focusPosition.current;
        // Test if data updated and we are past the last element

        if (!data || data.length <= focusIndex.current) {
          focusIndex.current = data ? data.length - 1 : 0;
        }
        // Test if focus positions updated and we are past the last one
        if (focusPositions <= focusPosition.current) {
          focusPosition.current = focusPositions - 1;
        }
        // Tell if our focus positions are greater than the data length
        if (data && data.length <= focusPosition.current) {
          focusPosition.current = data.length - 1;
        }
        // TODO: Test this code if needed
        // If we are focused past where the new list data ends, then move to the new pos in the list
        if (lastFocusPos !== focusIndex.current - focusPosition.current) {
          setOffset();
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, focusPositions]);

    // Moves the offset to the specified index. Not used for initial offsets
    const setOffset = (animated = false) => {
      const newIndex = focusIndex.current - focusPosition.current;

      if (flatListReference.current && data && newIndex < data.length) {
        flatListReference.current.scrollToIndex({
          index: newIndex > 0 ? newIndex : 0,
          animated,
          viewOffset: 0,
          viewPosition: 0,
        });
        setFirstFocus();
      }
    };

    const setFirstFocus = (focusReference: TouchableHighlight | null = null) => {
      const firstReference = focusReference ? focusReference : stateReferences.current[focusIndex.current];

      if (!firstFocusSet.current && firstReference) {
        firstFocusSet.current = true;

        if (listHasFocus.current) {
          helpers.FOCUS_NOW(firstReference);
        }
      }
    };

    const renderItemWrapped: ListRenderItem<any> = ({ item, index }) =>
      data && stateReferences ? (
        <FocusableFlatListItem
          isFirst={index === 0}
          isLast={index === data.length - 1}
          horizontal={horizontal}
          index={index}
          item={item}
          itemStyle={itemStyle}
          itemStyleFocused={itemStyleFocused}
          nextFocusDown={nextFocusDown}
          nextFocusLeft={nextFocusLeft}
          nextFocusRight={nextFocusRight}
          nextFocusUp={nextFocusUp}
          ref={thisReference => {
            setStateReferences(thisReference, index);
            // Test if we should start offset
            const initFocus = initialScrollIndex ? initialScrollIndex : 0;
            // If this is the correct first focus node, then set focusIndex

            if (index === initFocus && !firstFocusSet.current) {
              setFirstFocus(thisReference);
            }
          }}
          references={stateReferences}
          underlayColor={itemUnderlayColor}
          wrapItem={wrapItem}
          onFocus={() => {
            // On android screen reader will focus item before focus handle
            // Fix by fireing listGainedFocus for item focus as well
            if (helpers.isAndroid) {
              listGainedFocus?.();
            }
            // Make sure we have actually changed indicies
            if (index === focusIndex.current || !firstFocusSet.current) {
              return;
            }
            const isNext = focusIndex.current < index;
            const currentFocusPos = focusPosition.current;
            let newFocusPos = currentFocusPos;

            if (isNext) {
              if (currentFocusPos < focusPositions - 1) {
                newFocusPos = currentFocusPos + 1;
              }
            } else {
              if (currentFocusPos > 0) {
                newFocusPos = currentFocusPos - 1;
              }
            }
            const newIndex = index - newFocusPos;

            if (flatListReference.current && data && newIndex < data.length) {
              if (helpers.isWeb && window.focusOptionsPolyfill) {
                setTimeout(
                  () => {
                    flatListReference.current?.scrollToIndex({
                      index: newIndex >= 0 ? newIndex : 0,
                      animated: true,
                      viewOffset: 0,
                      viewPosition: 0,
                    });
                  },
                  horizontal ? 0 : 20,
                );
              } else {
                flatListReference.current.scrollToIndex({
                  index: newIndex >= 0 ? newIndex : 0,
                  animated: true,
                  viewOffset: 0,
                  viewPosition: 0,
                });
              }
            }
            focusIndex.current = index;
            focusPosition.current = newFocusPos;
            focusIndexChanged?.(index);
          }}
        >
          {renderItem(item, index)}
        </FocusableFlatListItem>
      ) : null;

    return (
      <>
        <FlatList
          {...properties}
          contentContainerStyle={
            horizontal
              ? {
                  marginLeft: paddingStart,
                  paddingRight: paddingEnd * 2,
                }
              : {
                  marginTop: paddingStart,
                  paddingBottom: paddingEnd * 2,
                }
          }
          data={data}
          disableScrollViewPanResponder
          horizontal={horizontal}
          initialScrollIndex={initialScrollIndex}
          keyExtractor={keyExtractor}
          overScrollMode={'always'}
          pagingEnabled={helpers.isAndroid}
          ref={flatListReference}
          removeClippedSubviews={false}
          renderItem={renderItemWrapped}
          scrollEnabled={false}
          scrollToOverflowEnabled
          style={style}
          onEndReached={() => {}}
        />
        <TouchableHighlight
          accessibilityHint={''}
          accessibilityLabel={' '}
          accessible
          importantForAccessibility={importantForAccessibility}
          nativeID={testID}
          ref={shouldUseContextReference ? contextReference : reference}
          style={styles.focusHandle}
          testID={testID}
          onFocus={e => {
            onFocus?.(e);
            listGainedFocus?.();

            if (firstFocusSet.current) {
              setTimeout(() => {
                const currentReference = stateReferences.current[focusIndex.current];

                helpers.FOCUS_NOW(currentReference);
              }, 10);
            } else {
              listHasFocus.current = true;
            }
          }}
        >
          <View />
        </TouchableHighlight>
      </>
    );
  },
);

type FocusableFlatListProps = FlatListProps<any> & {
  shouldUseContextReference?: boolean;
  onFocus?: (e: NativeSyntheticEvent<TargetedEvent>) => void;
  wrapItem?: boolean;
  paddingEnd?: number;
  paddingStart?: number;
  onRef?: (value: {
    scrollToIndex: (index: number, animated?: boolean | null) => void;
    focusToIndex: (index: number, forceFocus?: boolean) => void;
  }) => void;
  focusPositions?: number;
  focusIndexChanged?: (value: number) => void;
  listGainedFocus?: () => void;
  itemStyle?: StyleProp<ViewStyle>;
  itemStyleFocused?: StyleProp<ViewStyle>;
  itemUnderlayColor?: ColorValue;
  renderItem: (item: any, index: number) => ReactElement | null;
};

const styles = StyleSheet.create({
  focusHandle: {
    height: 1,
    opacity: 1,
    width: 1,
  },
});
